import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Plus, Filter } from 'lucide-react';
import { usePIM } from '../../context/PIMContext';
import { BASE_UPLOAD_URL } from '../../config';

const ComponentSelectionModal = ({ isOpen, onClose, onAdd }) => {
    const { productData } = usePIM();
    const [searchTerm, setSearchTerm] = useState('');
    const [potentialComponents, setPotentialComponents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'component', 'spare_part'

    useEffect(() => {
        if (isOpen) {
            fetchPotential();
        }
    }, [isOpen]);

    const fetchPotential = async () => {
        setLoading(true);
        try {
            // Fetch all products. 
            // Better to have a backend endpoint for this, but reusing GET /api/products is fine for now.
            // We need checks for 'component' OR 'spare_part'.
            // Backend filter ?role=... only supports one.
            // So fetch all and filter client side.
            const res = await fetch('/api/products', { // Remove status=Active to avoid excluding 'In Production' items
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                // Filter:
                // 1. Must have 'component' or 'spare_part' in product_types
                // 2. Must not be the current product itself
                const valid = data.filter(p => {
                    if (p.id === productData.id) return false;
                    const types = (typeof p.product_types === 'string' ? JSON.parse(p.product_types) : p.product_types) || [];
                    return types.includes('component') || types.includes('spare_part');
                });
                setPotentialComponents(valid);
            }
        } catch (err) {
            console.error("Failed to fetch potential components", err);
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = useMemo(() => {
        return potentialComponents.filter(item => {
            const matchesSearch =
                item.model_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.sku.toLowerCase().includes(searchTerm.toLowerCase());

            if (!matchesSearch) return false;

            if (roleFilter !== 'all') {
                const types = (typeof item.product_types === 'string' ? JSON.parse(item.product_types) : item.product_types) || [];
                return types.includes(roleFilter);
            }

            return true;
        });
    }, [potentialComponents, searchTerm, roleFilter]);


    const handleAdd = (item) => {
        // Determine default role to add as:
        // If item is ONLY spare_part, add as spare_part.
        // If item is ONLY component, add as component.
        // If both, maybe ask? Or default to component. 
        // For now, let's default to the role they filtered by, or 'component' if 'all'.

        const types = (typeof item.product_types === 'string' ? JSON.parse(item.product_types) : item.product_types) || [];
        let roleToAdd = 'component';
        if (types.includes('component')) roleToAdd = 'component';
        else if (types.includes('spare_part')) roleToAdd = 'spare_part';

        // We could show a second modal step to confirm role/notes, but let's keep it simple:
        // Pass to onAdd, let parent handle or assume default.
        // Actually onAdd expects (id, role, chargeId, notes).
        // Let's prompt for Role selection if ambiguous?
        // For MVP, just add as 'component' (Bill of Materials item) unless strictly spare part.

        onAdd(item.id, roleToAdd);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="bg-stone-900 border border-stone-700 rounded-xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="p-4 border-b border-stone-800 flex justify-between items-center bg-stone-950">
                    <div>
                        <h2 className="text-lg font-bold text-white">Add Component</h2>
                        <p className="text-xs text-stone-500">Select components or spare parts to add to this product.</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-stone-800 rounded-full text-stone-400 hover:text-white transition">
                        <X size={20} />
                    </button>
                </div>

                {/* Toolbar */}
                <div className="p-4 border-b border-stone-800 bg-stone-900/50 flex gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" size={16} />
                        <input
                            type="text"
                            placeholder="Search by name or SKU..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-stone-950 border border-stone-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition"
                            autoFocus
                        />
                    </div>
                    <div className="flex bg-stone-950 rounded-lg border border-stone-700 p-1">
                        {['all', 'component', 'spare_part'].map(f => (
                            <button
                                key={f}
                                onClick={() => setRoleFilter(f)}
                                className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${roleFilter === f
                                    ? 'bg-stone-800 text-white shadow-sm'
                                    : 'text-stone-500 hover:text-stone-300'
                                    }`}
                            >
                                {f === 'all' ? 'All Types' : f.replace('_', ' ').toUpperCase()}
                            </button>
                        ))}
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex justify-center items-center h-full text-stone-500">
                            Loading potential components...
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-stone-500 opacity-60">
                            <Filter size={48} className="mb-4" />
                            <p>No matching components found.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {filteredItems.map(item => (
                                <div key={item.id} className="bg-stone-950 border border-stone-800 rounded-lg p-3 hover:border-stone-600 transition group flex flex-col gap-3">
                                    <div className="flex gap-3">
                                        <div className="w-12 h-12 bg-stone-900 rounded border border-stone-800 overflow-hidden flex-shrink-0">
                                            {item.image_url ? (
                                                <img
                                                    src={`${BASE_UPLOAD_URL}/${item.image_url.startsWith('/') ? item.image_url.slice(1) : item.image_url}`}
                                                    alt={item.model_name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-xs text-stone-600">No Img</div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-sm text-stone-200 truncate" title={item.model_name}>{item.model_name}</h3>
                                            <p className="text-xs text-stone-500 truncate">{item.sku}</p>
                                            <div className="flex gap-1 mt-1">
                                                {(() => {
                                                    const types = (typeof item.product_types === 'string' ? JSON.parse(item.product_types) : item.product_types) || [];
                                                    return types.map(t => (
                                                        ['component', 'spare_part'].includes(t) && (
                                                            <span key={t} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-stone-900 border border-stone-800 text-stone-400">
                                                                {t.replace('_', ' ')}
                                                            </span>
                                                        )
                                                    ));
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleAdd(item)}
                                        className="w-full py-1.5 bg-stone-900 hover:bg-blue-600 hover:text-white border border-stone-800 text-stone-400 rounded text-xs font-medium transition flex items-center justify-center gap-2"
                                    >
                                        <Plus size={14} /> Add Pattern
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ComponentSelectionModal;
